import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HelpCircle, Search, ArrowRight, ChevronDown, ChevronRight, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { searchHowTo, searchRules } from "@/lib/howtoSearch";
import type { HowTo, HowToRole } from "@/data/howto";
import { RuleCard } from "@/components/RulePanel";
import { cn } from "@/lib/cn";

const EXAMPLES = [
  { es: "¿Cómo creo un evento que vean los miembros de la Junta?", en: "How do I create an event board members can see?" },
  { es: "¿Cuánto es la multa por una violación al reglamento?", en: "What is the fine for a rule violation?" },
  { es: "¿Qué necesito para instalar rejas o un aire acondicionado?", en: "What do I need to install security bars or A/C?" },
  { es: "¿Con cuánta anticipación se convoca la asamblea?", en: "How much notice is required to call the assembly?" },
];

export function AssistantWidget() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const isEn = i18n.language.startsWith("en");
  const tr = (es: string, en: string) => (isEn ? en : es);
  const role = (user?.role ?? null) as HowToRole | null;

  const results = useMemo(() => searchHowTo(query, role), [query, role]);
  const ruleResults = useMemo(() => searchRules(query), [query]);

  function goTo(route?: string) {
    setOpen(false);
    if (route) navigate(route);
  }

  return (
    <>
      {/* Floating button — present on every authenticated page */}
      <button
        onClick={() => setOpen(true)}
        aria-label={tr("Abrir asistente de ayuda", "Open help assistant")}
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-sm font-medium text-white shadow-lg hover:bg-primary-700 transition"
      >
        <HelpCircle size={18} />
        <span className="hidden sm:inline">{tr("Preguntar", "Ask")}</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/40 sm:p-6"
          onClick={() => setOpen(false)}
        >
          <div
            className="mt-10 w-full max-w-xl rounded-lg bg-white shadow-xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 p-4">
              <div>
                <h2 className="text-lg font-semibold">{tr("¿Cómo hago…?", "How do I…?")}</h2>
                <p className="text-xs text-slate-500">
                  {tr("Escriba lo que desea hacer y le mostramos los pasos.", "Type what you want to do and we’ll show the steps.")}
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-slate-100 rounded" aria-label={tr("Cerrar", "Close")}>
                <X size={18} />
              </button>
            </div>

            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center gap-2 rounded-md border border-slate-300 px-3 focus-within:border-primary">
                <Search size={16} className="text-slate-400 shrink-0" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={tr("Ej.: cómo creo un comunicado…", "e.g. how do I post an announcement…")}
                  className="w-full py-2 text-sm outline-none"
                />
                {query && (
                  <button onClick={() => setQuery("")} className="text-slate-400 hover:text-slate-600" aria-label={tr("Borrar", "Clear")}>
                    <X size={14} />
                  </button>
                )}
              </div>

              {!query && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-slate-500 mb-2">{tr("Ejemplos:", "Examples:")}</p>
                  <div className="flex flex-col gap-1.5">
                    {EXAMPLES.map((ex) => (
                      <button
                        key={ex.en}
                        onClick={() => setQuery(tr(ex.es, ex.en))}
                        className="text-left text-sm text-primary hover:underline"
                      >
                        {tr(ex.es, ex.en)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {results.length === 0 && ruleResults.length === 0 && (
                <p className="text-sm text-slate-500 py-6 text-center">
                  {tr(
                    "No encontramos una guía ni una regla para eso. Pruebe otras palabras o revise el Centro de Ayuda.",
                    "We couldn’t find a guide or a rule for that. Try other words or check the Help Center."
                  )}
                </p>
              )}

              {ruleResults.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    {tr("Reglas y normas", "Rules & regulations")}
                  </p>
                  {ruleResults.map(({ rule }) => (
                    <RuleCard key={rule.id} rule={rule} onNavigate={() => setOpen(false)} />
                  ))}
                </div>
              )}

              {results.length > 0 && (
                <div className="space-y-2">
                  {ruleResults.length > 0 && (
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      {tr("Cómo hacerlo", "How to")}
                    </p>
                  )}
                  {results.map(({ howto, allowed }) => (
                    <ResultCard
                      key={howto.id}
                      howto={howto}
                      allowed={allowed}
                      expanded={expandedId === howto.id}
                      onToggle={() => setExpandedId(expandedId === howto.id ? null : howto.id)}
                      onGo={() => goTo(howto.route)}
                      tr={tr}
                      isEn={isEn}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

interface ResultCardProps {
  howto: HowTo;
  allowed: boolean;
  expanded: boolean;
  onToggle: () => void;
  onGo: () => void;
  tr: (es: string, en: string) => string;
  isEn: boolean;
}

function ResultCard({ howto, allowed, expanded, onToggle, onGo, tr, isEn }: ResultCardProps) {
  return (
    <div className={cn("rounded-md border border-slate-200", !allowed && "opacity-70")}>
      <button onClick={onToggle} className="flex w-full items-center justify-between gap-2 p-3 text-left">
        <span className="text-sm font-medium">{isEn ? howto.titleEn : howto.titleEs}</span>
        {expanded ? <ChevronDown size={16} className="shrink-0 text-slate-400" /> : <ChevronRight size={16} className="shrink-0 text-slate-400" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3">
          <p className="text-xs text-slate-500">
            <span className="font-medium">{tr("Quién:", "Who:")}</span>{" "}
            {isEn ? howto.whoEn : howto.whoEs}
            {!allowed && (
              <span className="ml-2 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">
                {tr("Solo Junta/Admin", "Board/Admin only")}
              </span>
            )}
          </p>

          <ol className="list-decimal space-y-1.5 pl-5 text-sm text-slate-700">
            {howto.steps.map((s, i) => (
              <li key={i}>{isEn ? s.en : s.es}</li>
            ))}
          </ol>

          {(howto.noteEs || howto.noteEn) && (
            <p className="rounded bg-slate-50 p-2 text-xs text-slate-600">
              <span className="font-medium">{tr("Nota:", "Note:")}</span>{" "}
              {isEn ? howto.noteEn : howto.noteEs}
            </p>
          )}

          {howto.route && allowed && (
            <button onClick={onGo} className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
              {tr("Ir a la página", "Go to the page")} <ArrowRight size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
